// /weighbridge/settings/cameras — Phase C2 Group 2.
//
// Admin list of cameras configured for the tenant. Each row links to the
// detail page; "Add Camera" launches the same page in /new mode. Server
// shape: { cameras: WbCamera[] } with a nested yard + nullable
// weighbridge. The connection string is encrypted server-side and is
// never returned in any list/detail response.

import { useNavigate } from 'react-router-dom';
import { Camera, Plus } from 'lucide-react';
import { PageHeader } from '../../../components/ui/PageHeader/PageHeader';
import { Card } from '../../../components/ui/Card/Card';
import { Button } from '../../../components/ui/Button/Button';
import { Badge } from '../../../components/ui/Badge/Badge';
import { EmptyState } from '../../../components/ui/EmptyState/EmptyState';
import { useWbCameras, type WbCamera, type WbCameraType } from '../hooks/useWbCameras';
import { extractErrorMessage } from '../../../utils/errorMessage';
import styles from './WbCamerasPage.module.css';

const TYPE_LABEL: Record<WbCameraType, string> = {
  overview: 'Overview',
  anpr: 'ANPR',
  material: 'Material',
  plate: 'Plate',
};

const TYPE_CLASS: Record<WbCameraType, string> = {
  overview: styles.badgeOverview ?? '',
  anpr: styles.badgeAnpr ?? '',
  material: styles.badgeMaterial ?? '',
  plate: styles.badgePlate ?? '',
};

const PROTOCOL_LABEL: Record<string, string> = {
  rtsp: 'RTSP',
  onvif: 'ONVIF',
  http_mjpeg: 'HTTP MJPEG',
  usb: 'USB',
};

export function WbCamerasPage(): JSX.Element {
  const navigate = useNavigate();
  const camerasQuery = useWbCameras();
  const cameras = camerasQuery.data?.cameras ?? [];

  return (
    <div className={styles.page}>
      <PageHeader
        title="Cameras"
        subtitle="Configure cameras at each yard / weighbridge."
        actions={
          <Button
            variant="primary"
            icon={Plus}
            onClick={() => navigate('/weighbridge/settings/cameras/new')}
          >
            Add Camera
          </Button>
        }
      />

      {camerasQuery.isError ? (
        <p className={styles.error}>{extractErrorMessage(camerasQuery.error)}</p>
      ) : null}

      <Card flush>
        {camerasQuery.isLoading ? (
          <p className={styles.empty}>Loading…</p>
        ) : cameras.length === 0 ? (
          <EmptyState
            icon={Camera}
            title="No cameras configured"
            description="Add your first camera to start capturing imagery for tickets."
            action={
              <Button
                variant="primary"
                icon={Plus}
                onClick={() => navigate('/weighbridge/settings/cameras/new')}
              >
                Add Camera
              </Button>
            }
          />
        ) : (
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Name</th>
                <th>Type</th>
                <th>Protocol</th>
                <th>Bridge / Yard</th>
                <th>Active</th>
                <th aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {cameras.map((c: WbCamera) => (
                <tr key={c.enc_id}>
                  <td>{c.name}</td>
                  <td>
                    <Badge className={TYPE_CLASS[c.type]}>{TYPE_LABEL[c.type]}</Badge>
                  </td>
                  <td>{PROTOCOL_LABEL[c.protocol] ?? c.protocol}</td>
                  <td>{c.weighbridge?.name ?? c.yard.name}</td>
                  <td>{c.is_active ? 'Yes' : 'No'}</td>
                  <td className={styles.actions}>
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() =>
                        navigate(
                          `/weighbridge/settings/cameras/${encodeURIComponent(c.enc_id)}`,
                        )
                      }
                    >
                      Edit
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>
    </div>
  );
}
